import Tooltip from '@anm/components/Tooltip';
import isDev from '@anm/helpers/is/isDev';
import { getUpgradeLinkProps } from '@anm/helpers/upgradeLink';
import useOutsideClick from '@anm/hooks/useOutsideClick';
import useSwitchState from '@anm/hooks/useSwitchState';
import React, { useRef, FC } from 'react';

import { TooltipButton, TooltipText, Wrapper } from './Wrapper';

export type TooltipDirection = 'to-left' | 'to-right' | 'center';

type UpgradeTooltipProps = {
  canShow: boolean;
  className?: string;
  buttonTarget?: string;
  onClose?(): void;
} & Partial<TooltipContentProps> &
  Partial<TooltipTextData>;

type TooltipTextData = {
  text: string;
  buttonText: string;
  buttonLink: string;
};

export type TooltipContentProps = {
  tooltipDirection: TooltipDirection;
  minWidth?: number;
  showToTop?: boolean;
};

const tooltipDefaults: TooltipTextData = {
  text: `Upgrade to a premium plan to set up your brand`,
  buttonText: 'See premium plans',
  buttonLink: `https://wave${isDev() ? '-test' : ''}.video/pricing`
};

const UpgradeTooltip: FC<UpgradeTooltipProps> = ({
  children,
  canShow,
  showToTop,
  buttonTarget,
  minWidth = 224,
  tooltipDirection = 'to-left',
  className,
  text = tooltipDefaults.text,
  buttonText = tooltipDefaults.buttonText,
  buttonLink = tooltipDefaults.buttonLink,
  onClose
}) => {
  const [isTooltipOpened, openTooltip, closeTooltip] = useSwitchState();

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => {
    onClose?.();
    closeTooltip();
  });

  return (
    <Wrapper {...{ ref, showToTop, minWidth, tooltipDirection }} onClick={openTooltip}>
      <Tooltip
        className={className}
        isOpened={canShow && isTooltipOpened}
        {...(canShow && {
          renderContent: () => (
            <>
              <TooltipText>{text}</TooltipText>
              <TooltipButton variant="light-blue" {...getUpgradeLinkProps(buttonLink, buttonTarget)} newWindow={true} size="medium">
                {buttonText}
              </TooltipButton>
            </>
          )
        })}
      >
        {children}
      </Tooltip>
    </Wrapper>
  );
};
export default UpgradeTooltip;
